import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Box,
  AppBar,
  Toolbar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { useNavigate, useLocation } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { IfPermission } from "./permission/IfPermission";
import { ADMIN_PERMISSION_EDIT, ADMIN_PERMISSION_VIEW, LS_DOCUMENTS_VIEW } from "./permission/permission-consts";
import lsLogo from "../assets/LegalStreamLogo.avif";
import ProfileMenu from "./profile/ProfileMenu";

const drawerWidth = 240;
const collapsedWidth = 60;

const Header = ({ onSignOut }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const handleToggle = () => setOpen(!open);

  const handleNavigation = (route) => {
    navigate(route);
  };

  // Check if any Access Control child is selected
  const accessControlSelected = ["/access-control/users", "/access-control/roles", "/access-control/permissions"].includes(location.pathname);

  const getMainItemSx = (isOpen) => (
    isOpen ? {
      px: 2,
      py: 1.5,
      color: "#fff",
      "&.Mui-selected": {
        backgroundColor: "rgba(255, 255, 255, 0.15)",
        borderLeft: "4px solid #fff",
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" }
      },
      "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" }
    } : {
      px: 1.5,
      py: 1.5,
      justifyContent: "center",
      color: "#fff",
      "&.Mui-selected": {
        backgroundColor: "rgba(255, 255, 255, 0.15)",
        borderLeft: "4px solid #fff",
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" }
      },
      "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" }
    }
  );

  const getNestedItemSx = (isOpen) => (
    isOpen ? {
      pl: 7,
      pr: 2,
      py: 1,
      color: "#fff",
      "&.Mui-selected": {
        backgroundColor: "rgba(255, 255, 255, 0.15)",
        borderLeft: "4px solid #4fc3f7",
        "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" }
      },
      "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.08)" }
    } : {
      display: "none"
    }
  );


  return (
    <>
      {/* Top AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgb(0, 84, 165)",
          boxShadow: "none"
        }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "flex-end" }}>
          <ProfileMenu onSignOut={onSignOut} />
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: open ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : collapsedWidth,
            transition: "width 0.3s",
            overflowX: "hidden",
            boxSizing: "border-box",
            backgroundColor: "rgb(0, 84, 165)",
            color: "#fff",
            position: "fixed",
            height: "100vh",
            top: 0,
            paddingTop: 0,
            zIndex: 1201
          },
        }}
      >

        {/* Header: Logo when expanded, Breadcrumb + Toggle when collapsed */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1,
            minHeight: open ? 64 : 40,
          }}
        >
          {open ? (
            <>
              {/* Logo */}
              <img
                onClick={() => handleNavigation("/home")}
                src={lsLogo}
                alt="Company Logo"
                style={{ width: "120px", paddingLeft: '10px', height: "auto", cursor: "pointer" }}
              />

              {/* Collapse Icon */}
              <Tooltip title="Collapse menu" placement="right">
                <IconButton onClick={handleToggle} sx={{ color: "#fff" }}>
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            // Collapsed state: only expand icon
            <Tooltip title="Expand menu" placement="right">
              <IconButton onClick={handleToggle} sx={{ color: "#fff" }}>
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <List sx={{ pt: 0 }}>

          {/* ======================= */}
          {/* Dashboard */}
          {/* ======================= */}
          <Tooltip title="Dashboard" placement="right" disableHoverListener={open}>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === "/dashboard" || location.pathname === "/home"}
                onClick={() => handleNavigation("/dashboard")}
                sx={getMainItemSx(open)}
              >
                <ListItemIcon sx={{ color: "#fff", minWidth: open ? 36 : "auto" }}>
                  <DashboardOutlinedIcon />
                </ListItemIcon>
                {open && <ListItemText primary="Dashboard" />}
              </ListItemButton>
            </ListItem>
          </Tooltip>

          {/* ======================= */}
          {/* Document Section */}
          {/* ======================= */}
          <IfPermission permission={LS_DOCUMENTS_VIEW}>
            <Tooltip title="Document" placement="right" disableHoverListener={open}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={location.pathname === "/document"}
                  onClick={() => handleNavigation("/document")}
                  sx={getMainItemSx(open)}
                >
                  <ListItemIcon sx={{ color: "#fff", minWidth: open ? 36 : "auto" }}>
                    <DocumentScannerOutlinedIcon />
                  </ListItemIcon>
                  {open && <ListItemText primary="Document" />}
                </ListItemButton>
              </ListItem>
            </Tooltip>
          </IfPermission>

          {/* ======================= */}
          {/* Access Control Section */}
          {/* ======================= */}
          <IfPermission anyPermissions={[ADMIN_PERMISSION_VIEW, ADMIN_PERMISSION_EDIT]}>
            {/* Parent "Access Control" */}
            <Tooltip title="Access Control" placement="right" disableHoverListener={open}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={accessControlSelected}
                  onClick={() => handleNavigation("/access-control/users")}
                  sx={getMainItemSx(open)}
                >
                  <ListItemIcon sx={{ color: "#fff", minWidth: open ? 36 : "auto" }}>
                    <AdminPanelSettingsIcon />
                  </ListItemIcon>
                  {open && <ListItemText primary="Access Control" />}
                </ListItemButton>
              </ListItem>
            </Tooltip>

            {/* Users */}
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === "/access-control/users"}
                onClick={() => handleNavigation("/access-control/users")}
                sx={getNestedItemSx(open)}
              >
                {open && <ListItemText primary="Users" />}
              </ListItemButton>
            </ListItem>

            {/* Roles */}
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === "/access-control/roles"}
                onClick={() => handleNavigation("/access-control/roles")}
                sx={getNestedItemSx(open)}
              >
                {open && <ListItemText primary="Roles" />}
              </ListItemButton>
            </ListItem>

            {/* Permissions */}
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname === "/access-control/permissions"}
                onClick={() => handleNavigation("/access-control/permissions")}
                sx={getNestedItemSx(open)}
              >
                {open && <ListItemText primary="Permissions" />}
              </ListItemButton>
            </ListItem>

          </IfPermission>
        </List>

      </Drawer>

    </>
  );
};

export default Header;